<script setup lang="ts">
import { first } from "lodash";
import { onMounted, type Ref, ref, watch } from "vue";
import type { ProductResponse } from "../../../responses/ProductResponse";
import type { ProductVariantResponse } from "../../../responses/ProductVariantResponse";
import { label } from "../../mixins/translations";
import { useCartStore } from "../../stores/cart";
import CustomNumberInput from "../CustomNumberInput.vue";
import Modal from "../Modal.vue";

// import {label} from "../../mixins/translations";

interface Props {
    product: ProductResponse;
}
const props = defineProps<Props>();
const selectedVariant: Ref<ProductVariantResponse> = ref({});
const loading: Ref<boolean> = ref(false);
const amount: Ref<number> = ref(1);
const cart = useCartStore();

const emit = defineEmits<(e: "close") => void>();

const addToCart = () => {
    loading.value = true;
    cart.addItem({
        amount: amount.value,
        variant_id: selectedVariant.value.id,
    }).then((response) => {
        // window.trackAddToCartGoogle(this.product, this.amount, 'add_to_cart');
        amount.value = 1;
        loading.value = false;
        emit("close");
    });
};

const updateAmount = (newAmount) => {
    amount.value = newAmount;
};

const updateOpen = (open) => {
    if (!open) {
        emit("close");
    }
};

onMounted(() => {
    selectedVariant.value = first(props.product.variants);
});
</script>

<template>
    <Modal @updateOpen="updateOpen" :open="product != null">
        <template #trigger-wrapper>&nbsp;</template>
        <template #title>
            <div class="w-full">
                <div class="text-xl w-full text-primary-900 font-bold">{{ product.name }}</div>
                <div class="flex flex-col gap-4 mt-2">
                    <div class="flex gap-4">
                        <div class="flex-1">
                            <p class="text-sm text-secondary-700 leading-relaxed">{{ label('site.product.select_variant') }}</p>
                            <select v-model="selectedVariant" class="w-full text-sm border-secondary-200 rounded-xl py-4 px-3">
                                <option :value="variant" v-for="variant in product.variants">
                                    {{ variant.name }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <p class="text-sm text-secondary-700 leading-relaxed">{{ label('site.product.quantity') }}</p>
                            <CustomNumberInput :min="1" @input="updateAmount" :value="amount"></CustomNumberInput>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #description>
            <div class="flex flex-row gap-x-4">
                <button type="button" @click="emit('close')"
                        class="md:block w-6/12 h-10 px-5 pointer rounded-xl font-medium flex items-center justify-center text-foreground bg-secondary-100 hover:bg-secondary-200 transition-all duration-500">
                    {{ label('site.cart.cancel') }}
                </button>
                <button @click.prevent="addToCart" :class="{'loading': loading}"
                        class="h-10 px-5 w-6/12 pointer rounded-xl flex items-center justify-center text-primary-foreground bg-primary hover:bg-primary-600 transition-all duration-500 z-20">
                    <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" >
                        <circle cx="8" cy="21" r="1"/>
                        <circle cx="19" cy="21" r="1"/>
                        <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
                    </svg>
                    <span>{{ label('site.product.add_to_cart') }}</span>
                </button>
            </div>
        </template>
    </Modal>
</template>
